import React from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { BLACK, FEED_BACK } from '../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../theme/fontConstants';
import SectionCard from '../../positionTemplate/components/SectionCard';

import ChecklistItem from './ChecklistItem';

export const BANNER_STATUS = {
  READY: 'ready',
  DRAFT_ONLY: 'draftOnly',
  INCOMPLETE: 'incomplete',
};

const BANNER_STYLES = {
  [BANNER_STATUS.READY]: {
    backgroundColor: FEED_BACK.GREEN_00,
    borderColor: FEED_BACK.GREEN_10,
    color: FEED_BACK.GREEN_80,
    Icon: CheckIcon,
  },
  [BANNER_STATUS.DRAFT_ONLY]: {
    backgroundColor: FEED_BACK.YELLOW_10,
    borderColor: FEED_BACK.YELLOW_50,
    color: FEED_BACK.YELLOW_100,
    Icon: InfoOutlinedIcon,
  },
  [BANNER_STATUS.INCOMPLETE]: {
    backgroundColor: BLACK[10],
    borderColor: BLACK[30],
    color: BLACK[70],
    Icon: InfoOutlinedIcon,
  },
};

const BANNER_MESSAGE_KEYS = {
  [BANNER_STATUS.READY]: 'positionTemplate.create.beforeYouSave.banner.ready',
  [BANNER_STATUS.DRAFT_ONLY]: 'positionTemplate.create.beforeYouSave.banner.draftOnly',
  [BANNER_STATUS.INCOMPLETE]: 'positionTemplate.create.beforeYouSave.banner.incomplete',
};

export const getBannerContent = (canCreate, canApprove) => {
  let status = BANNER_STATUS.INCOMPLETE;
  if (canCreate && canApprove) {
    status = BANNER_STATUS.READY;
  } else if (canCreate) {
    status = BANNER_STATUS.DRAFT_ONLY;
  }
  return { status, messageKey: BANNER_MESSAGE_KEYS[status] };
};

const checkShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  labelKey: PropTypes.string.isRequired,
  isMet: PropTypes.bool.isRequired,
});

const GroupLabel = ({ labelKey }) => (
  <Typography
    sx={{
      color: BLACK[60],
      fontFamily: PRIMARY_FONT[700],
      fontSize: '11px',
      letterSpacing: '0.06em',
      marginBottom: '4px',
      textTransform: 'uppercase',
    }}
  >
    {LanguageConverterV2(labelKey)}
  </Typography>
);

GroupLabel.propTypes = { labelKey: PropTypes.string.isRequired };

const BeforeYouSaveCard = ({ createChecks, approveChecks, canCreate, canApprove }) => {
  const { status, messageKey } = getBannerContent(canCreate, canApprove);
  const { Icon, ...bannerColors } = BANNER_STYLES[status];
  const title = LanguageConverterV2('positionTemplate.create.beforeYouSave.title');
  const message = LanguageConverterV2(messageKey);

  return (
    <SectionCard
      header={
        <Typography sx={{ color: BLACK[100], fontFamily: PRIMARY_FONT[700], fontSize: '14px' }}>
          {title}
        </Typography>
      }
    >
      <Box sx={{ padding: '8px 0' }}>
        <GroupLabel labelKey="positionTemplate.create.beforeYouSave.requiredToCreate" />
        {createChecks.map((check) => (
          <ChecklistItem key={check.id} labelKey={check.labelKey} isComplete={check.isMet} />
        ))}
        <Box sx={{ borderTop: `1px solid ${BLACK[20]}`, margin: '12px 0' }} />
        <GroupLabel labelKey="positionTemplate.create.beforeYouSave.requiredToApprove" />
        {approveChecks.map((check) => (
          <ChecklistItem key={check.id} labelKey={check.labelKey} isComplete={check.isMet} />
        ))}
        <Box
          data-testid="before-you-save-banner"
          data-status={status}
          sx={{
            alignItems: 'flex-start',
            backgroundColor: bannerColors.backgroundColor,
            border: `1px solid ${bannerColors.borderColor}`,
            borderRadius: '6px',
            color: bannerColors.color,
            display: 'flex',
            gap: '8px',
            marginTop: '12px',
            padding: '10px 12px',
          }}
        >
          <Icon sx={{ fontSize: '14px', marginTop: '3px' }} />
          <Typography sx={{ color: 'inherit', fontFamily: PRIMARY_FONT[700], fontSize: '13px' }}>
            {message}
          </Typography>
        </Box>
      </Box>
    </SectionCard>
  );
};

BeforeYouSaveCard.propTypes = {
  createChecks: PropTypes.arrayOf(checkShape).isRequired,
  approveChecks: PropTypes.arrayOf(checkShape).isRequired,
  canCreate: PropTypes.bool.isRequired,
  canApprove: PropTypes.bool.isRequired,
};

export default BeforeYouSaveCard;
